import { useEffect, useId, useState } from 'react';
import { DEFAULT_TIMER_MS, type BoardDoc } from '../collab/BoardDoc';
import { useTimer } from '../collab/hooks';
import { Icon } from './icons';
import { PanelDivider, Popover } from './controls';

const PRESETS_MIN = [5, 15, 25, 45, 60];

function format(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function msToParts(ms: number): { minutes: number; seconds: number } {
  const total = Math.max(0, Math.round(ms / 1000));
  return { minutes: Math.floor(total / 60), seconds: total % 60 };
}

function partsToMs(minutes: number, seconds: number): number {
  const m = Number.isFinite(minutes) ? Math.max(0, Math.min(999, Math.floor(minutes))) : 0;
  const s = Number.isFinite(seconds) ? Math.max(0, Math.min(59, Math.floor(seconds))) : 0;
  return (m * 60 + s) * 1000;
}

/**
 * The ticking view of the shared timer. Both the chip on the trigger and the
 * panel behind it need to count down, and they are not always rendered
 * together — the compact top bar shows the panel on its own.
 */
function useCountdown(doc: BoardDoc) {
  const timer = useTimer(doc);
  const [now, setNow] = useState(() => Date.now());
  const running = timer.running && timer.endAt !== null;

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [running]);

  const remaining = running ? timer.endAt! - now : timer.durationMs;
  return { timer, running, remaining, done: running && remaining <= 0 };
}

function DurationField({
  id,
  label,
  value,
  min,
  max,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  onChange: (next: number) => void;
}) {
  return (
    <label className="timer-duration-field" htmlFor={id}>
      <input
        id={id}
        className="timer-duration-input"
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={1}
        value={value}
        disabled={disabled}
        aria-label={label}
        onChange={(e) => {
          const raw = e.target.value === '' ? 0 : Number(e.target.value);
          if (!Number.isFinite(raw)) return;
          onChange(Math.max(min, Math.min(max, Math.floor(raw))));
        }}
        onFocus={(e) => e.currentTarget.select()}
      />
      <span className="timer-duration-unit">{label}</span>
    </label>
  );
}

/** The countdown itself, mountable outside its own popover. */
export function TimerPanel({ doc }: { doc: BoardDoc }) {
  const { timer, running, remaining, done } = useCountdown(doc);
  const canEdit = doc.canEdit();
  const baseId = useId();
  const parts = msToParts(timer.durationMs);
  const [draftMin, setDraftMin] = useState(parts.minutes);
  const [draftSec, setDraftSec] = useState(parts.seconds);

  useEffect(() => {
    if (running) return;
    const next = msToParts(timer.durationMs);
    setDraftMin(next.minutes);
    setDraftSec(next.seconds);
  }, [timer.durationMs, running]);

  const applyDuration = (durationMs: number) => {
    const clamped = Math.max(1000, durationMs);
    if (!running) doc.setTimer({ durationMs: clamped, endAt: null, running: false });
  };

  const setFromParts = (minutes: number, seconds: number) => {
    setDraftMin(minutes);
    setDraftSec(seconds);
    applyDuration(partsToMs(minutes, seconds));
  };

  const start = (durationMs = timer.durationMs) => {
    const ms = Math.max(1000, durationMs);
    doc.setTimer({ running: true, endAt: Date.now() + ms, durationMs: ms });
  };
  const pause = () => {
    if (timer.endAt !== null) doc.setTimer({ running: false, durationMs: Math.max(0, timer.endAt - Date.now()) });
  };
  const reset = () => {
    doc.setTimer({ running: false, endAt: null, durationMs: DEFAULT_TIMER_MS });
    setDraftMin(45);
    setDraftSec(0);
  };

  const activePreset = !running && draftSec === 0 ? draftMin : null;

  return (
    <div className="popover-stack timer-panel">
      <div className={`timer-display ${done ? 'is-done' : ''}`}>{format(remaining)}</div>
      {canEdit && (
        <>
          <div className="icon-row timer-actions">
            {!running ? (
              <button type="button" className="primary-btn" onClick={() => start(partsToMs(draftMin, draftSec))}>
                <Icon name="play" size={14} /> Старт
              </button>
            ) : (
              <button type="button" className="primary-btn" onClick={pause}>
                Пауза
              </button>
            )}
            <button type="button" className="menu-item" onClick={reset}>
              <Icon name="clock" size={14} /> Сброс
            </button>
          </div>

          <PanelDivider />

          <div className="timer-duration-card" aria-disabled={running || undefined}>
            <div className="popover-label">Своё время</div>
            <div className="timer-duration-row">
              <DurationField
                id={`${baseId}-min`}
                label="мин"
                value={draftMin}
                min={0}
                max={999}
                disabled={running}
                onChange={(minutes) => setFromParts(minutes, draftSec)}
              />
              <span className="timer-duration-sep" aria-hidden="true">
                :
              </span>
              <DurationField
                id={`${baseId}-sec`}
                label="сек"
                value={draftSec}
                min={0}
                max={59}
                disabled={running}
                onChange={(seconds) => setFromParts(draftMin, seconds)}
              />
            </div>

            <div className="popover-label">Быстрый выбор</div>
            <div className="timer-preset-grid" role="group" aria-label="Быстрый выбор длительности">
              {PRESETS_MIN.map((min) => (
                <button
                  key={min}
                  type="button"
                  className={`timer-preset ${activePreset === min ? 'is-active' : ''}`}
                  disabled={running}
                  aria-pressed={activePreset === min}
                  onClick={() => setFromParts(min, 0)}
                >
                  <span className="timer-preset-value">{min}</span>
                  <span className="timer-preset-unit">мин</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** Shared countdown, for timeboxing a workshop activity in front of everyone. */
export function TimerWidget({ doc }: { doc: BoardDoc }) {
  const { timer, running, remaining, done } = useCountdown(doc);

  return (
    <Popover
      title="Таймер"
      trigger={() => (
        <>
          <Icon name="timer" size={16} />
          {(running || timer.endAt !== null) && (
            <span className={`timer-chip ${done ? 'is-done' : ''}`}>{format(remaining)}</span>
          )}
        </>
      )}
    >
      {() => <TimerPanel doc={doc} />}
    </Popover>
  );
}
